import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  List as ListIcon,
  Clock,
  ChevronDown,
  Loader2,
  Sparkles,
  Zap,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FolderOpen,
  Search,
  Check,
  LayoutTemplate,
} from "lucide-react";
import documentParseDataService from "../../services/document-parse-data.service";
import documentParsingService, {
  Template,
} from "../../services/document-parsing.service";
import uploadedFilesService, {
  UploadedFile,
} from "../../services/uploaded-files.service";
import documentStoresService from "../../services/document-stores.service";
import Loader from "../../widgets/Loader";
import Can from "../../components/auth/Can";
import { JsonViewPopup } from "./JsonViewPopup";
import { LogsPopup } from "./LogsPopup";
import MultiSelectDropdown from "../../components/documents/MultiSelectDropdown";

interface DocumentStoreOption {
  _id: string;
  folder_name: string;
  folder_description?: string;
  document_store_type?: string;
  id: string;
}

const ParseFile: React.FC = () => {
  const [documentStoreId, setDocumentStoreId] = useState<string>("");
  const [documentStores, setDocumentStores] = useState<DocumentStoreOption[]>(
    [],
  );
  const [parseData, setParseData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [showJsonPopup, setShowJsonPopup] = useState(false);
  const [showLogsPopup, setShowLogsPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");

  const [templates, setTemplates] = useState<Template[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [accuracyLevel, setAccuracyLevel] = useState<string>("HIGH");
  const [isParsing, setIsParsing] = useState(false);
  const pollingIntervalRef = useRef<any | null>(null);

  // --- Custom Dropdown States ---
  // Folder
  const [isFolderOpen, setIsFolderOpen] = useState(false);
  const [folderSearchTerm, setFolderSearchTerm] = useState("");
  const folderRef = useRef<HTMLDivElement>(null);

  // Template
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const templateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDocumentStores();

    // Click outside listener for dropdowns
    const handleClickOutside = (event: MouseEvent) => {
      // Folder Dropdown
      if (
        folderRef.current &&
        !folderRef.current.contains(event.target as Node)
      ) {
        setIsFolderOpen(false);
      }
      // Template Dropdown
      if (
        templateRef.current &&
        !templateRef.current.contains(event.target as Node)
      ) {
        setIsTemplateOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (documentStoreId) {
      fetchParseData();
      fetchTemplates();
      fetchUploadedFiles();
    }
  }, [documentStoreId, currentPage]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchDocumentStores = async () => {
    try {
      const response = await documentStoresService.getDocumentStoresDropdown();
      if (response.success) {
        setDocumentStores(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching document stores:", error);
      toast.error("Failed to fetch document stores");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchParseData = async (silent: boolean = false) => {
    if (!documentStoreId) return;

    if (!silent) {
      setIsLoading(true);
    }
    try {
      const response = await documentParseDataService.getDocumentParseDataList({
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        document_store_id: documentStoreId,
      });

      if (response.success) {
        setParseData(response.data.documentparseDataList);
        setTotalCount(response.data.total_count);

        setSelectedItem((prevSelectedItem: any) => {
          if (prevSelectedItem) {
            const updatedItem = response.data.documentparseDataList.find(
              (item: any) => item._id === prevSelectedItem._id,
            );
            return updatedItem || prevSelectedItem;
          }
          return prevSelectedItem;
        });

        const needsPolling = response.data.documentparseDataList.some(
          (item) =>
            item.document_parse_status &&
            item.document_parse_status !== "DONE" &&
            item.document_parse_status !== "FAILED",
        );

        if (needsPolling) {
          startStatusPolling();
        }
      } else {
        if (!silent) {
          toast.error("Failed to fetch parsed files");
        }
      }
    } catch (error: any) {
      console.error("Error fetching parse data:", error);
      if (!silent) {
        toast.error("Failed to fetch parsed files");
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const startStatusPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    const pollStatus = async () => {
      if (!documentStoreId) return;

      setParseData((currentData) => {
        const processingFiles = currentData.filter(
          (item) =>
            item.document_parse_status &&
            item.document_parse_status !== "DONE" &&
            item.document_parse_status !== "FAILED",
        );

        if (processingFiles.length === 0) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return currentData;
        }

        const parseIds = processingFiles.map((item) => item._id);

        documentParseDataService
          .getStatusByIds({
            document_parse_ids: parseIds,
            document_store_id: documentStoreId,
          })
          .then((response) => {
            if (response?.data && Array.isArray(response.data)) {
              const completedItems = response.data.filter(
                (update) =>
                  update.document_parse_status === "DONE" ||
                  update.document_parse_status === "FAILED",
              );

              setParseData((prevData: any) => {
                const updatedData = prevData.map((item: any) => {
                  const statusUpdate: any = response.data.find(
                    (update) => update._id === item._id,
                  );
                  if (statusUpdate) {
                    return {
                      ...item,
                      document_parse_status: statusUpdate.document_parse_status,
                      document_parse_duration:
                        statusUpdate.document_parse_duration,
                    };
                  }
                  return item;
                });

                setSelectedItem((prevSelectedItem: any) => {
                  if (prevSelectedItem) {
                    const updatedItem = updatedData.find(
                      (item: any) => item._id === prevSelectedItem._id,
                    );
                    return updatedItem || prevSelectedItem;
                  }
                  return prevSelectedItem;
                });

                const stillProcessing = updatedData.some(
                  (item: any) =>
                    item.document_parse_status &&
                    item.document_parse_status !== "DONE" &&
                    item.document_parse_status !== "FAILED",
                );

                if (!stillProcessing && pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                  if (completedItems.length > 0) {
                    fetchParseData(true);
                    toast.success("Files are being parsed successfully!");
                  }
                }

                return updatedData;
              });
            }
          })
          .catch((error) => {
            console.error("Error polling parse statuses:", error);
          });

        return currentData;
      });
    };

    pollingIntervalRef.current = setInterval(pollStatus, 3000);
  };

  const fetchTemplates = async () => {
    try {
      const response = await documentParsingService.getTemplatesDropdown();
      if (response.success) {
        setTemplates(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to fetch templates");
    }
  };

  const fetchUploadedFiles = async () => {
    if (!documentStoreId) return;

    try {
      const response = await uploadedFilesService.getUploadedFilesDropdown({
        document_store_id: documentStoreId,
      });
      if (response.success) {
        setUploadedFiles(response.data.uploadedFilesList);
      }
    } catch (error: any) {
      console.error("Error fetching uploaded files:", error);
      toast.error("Failed to fetch uploaded files");
    }
  };

  const handleParse = async () => {
    if (!documentStoreId) return;

    if (selectedFileIds.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    if (!accuracyLevel) {
      toast.error("Please select an accuracy level");
      return;
    }

    setIsParsing(true);
    try {
      const response = await documentParsingService.processFiles(
        documentStoreId,
        {
          ai_performance_level: accuracyLevel,
          file_ids: selectedFileIds,
          schema_id: selectedTemplateId,
        },
      );

      if (response.success) {
        setSelectedTemplateId("");
        setSelectedFileIds([]);
        setTimeout(() => {
          fetchParseData();
          startStatusPolling();
        }, 1000);
      } else {
        toast.error("Failed to parse files");
      }
    } catch (error: any) {
      console.error("Error parsing files:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to parse files");
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleViewResult = (data: any) => {
    setSelectedData(data.result || data);
    const fileNames =
      data.file_data?.map((file: any) => file.name).join(", ") || "Unknown";
    const displayName =
      data.file_data?.length > 1 ? `${data.file_data.length} files` : fileNames;
    setPopupTitle(`Parse Result - ${displayName}`);
    setShowJsonPopup(true);
  };

  const handleViewLogs = (data: any) => {
    setSelectedItem(data);
    const fileNames =
      data.file_data?.map((file: any) => file.name).join(", ") || "Unknown";
    const displayName =
      data.file_data?.length > 1 ? `${data.file_data.length} files` : fileNames;
    setPopupTitle(`Processing Details - ${displayName}`);
    setShowLogsPopup(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // --- Handle Dropdown Selections ---
  const handleFolderSelect = (folderId: string) => {
    setDocumentStoreId(folderId);
    setSelectedTemplateId("");
    setSelectedFileIds([]);
    setParseData([]);
    setTotalCount(0);
    setCurrentPage(1);
    setIsFolderOpen(false);
    setFolderSearchTerm("");
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setIsTemplateOpen(false);
    setTemplateSearchTerm("");
  };

  // --- Filtering Helpers ---
  const filteredFolders = documentStores.filter((store) =>
    store.folder_name.toLowerCase().includes(folderSearchTerm.toLowerCase()),
  );
  const selectedFolderObj = documentStores.find(
    (store) => store.id === documentStoreId,
  );

  const filteredTemplates = templates.filter((template) =>
    template.schema_name
      .toLowerCase()
      .includes(templateSearchTerm.toLowerCase()),
  );
  const selectedTemplateObj = templates.find(
    (t) => t._id === selectedTemplateId,
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: any) => {
    switch (status) {
      case "DONE":
        return {
          color: "bg-emerald-50 text-emerald-700 border border-emerald-200",
          text: "Completed",
          icon: CheckCircle2,
        };
      case "FAILED":
        return {
          color: "bg-rose-50 text-rose-700 border border-rose-200",
          text: "Failed",
          icon: XCircle,
        };
      case "IN_PROGRESS":
      case "PROCESSING":
        return {
          color: "bg-blue-50 text-blue-700 border border-blue-200",
          text: "Processing",
          icon: Loader2,
        };
      case "PENDING":
        return {
          color: "bg-amber-50 text-amber-700 border border-amber-200",
          text: "Pending",
          icon: AlertCircle,
        };
      default:
        return {
          color: "bg-slate-50 text-slate-700 border border-slate-200",
          text: status || "Unknown",
          icon: AlertCircle,
        };
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
      {/* Modern Header with Glass Morphism */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="px-6 py-3">
          {/* Page Title & Description */}
          <div className="mb-3">
            <div className="flex items-center gap-3">
              {/* Icon */}
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600">
                <Sparkles className="h-4 w-4 text-white" />
              </div>

              {/* Title + Description */}
              <div>
                <h1 className="text-lg font-bold text-slate-900">
                  AI File Parsing
                </h1>
                <p className="text-xs text-slate-600">
                  Parse and extract structured data from folder using AI-powered
                  conversion
                </p>
              </div>
            </div>
          </div>

          {/* Control Panel */}
          <div className="flex flex-col gap-3">
            {/* Document Store Selection - Compact */}
            <div className="grid grid-cols-12 gap-3 items-end">
              {/* --- CUSTOM FOLDER DROPDOWN (WITH SEARCH) --- */}
              <div className="col-span-3" ref={folderRef}>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                  My Folders
                  <span className="text-rose-600">*</span>
                </label>
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => setIsFolderOpen(!isFolderOpen)}
                    className={`relative w-full text-left px-3 py-2.5 pl-10 bg-white border-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all duration-300 font-semibold shadow-sm hover:shadow-md flex items-center justify-between rounded-md
                      ${
                        isFolderOpen
                          ? "border-blue-500 ring-1 ring-blue-500"
                          : "border-slate-200 hover:border-blue-300"
                      }
                    `}
                  >
                    <span className="truncate block pr-6">
                      {selectedFolderObj ? (
                        <>
                          {selectedFolderObj.folder_name}
                          {selectedFolderObj.document_store_type && (
                            <span className="text-slate-500 font-normal ml-1">
                              • {selectedFolderObj.document_store_type}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-500 text-opacity-80">
                          Select a folder...
                        </span>
                      )}
                    </span>

                    {/* Icons */}
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <FolderOpen className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ChevronDown
                        className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                          isFolderOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>

                  {/* Dropdown Panel */}
                  {isFolderOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl animate-in fade-in zoom-in-95 duration-100">
                      {/* Search Input */}
                      <div className="p-2 border-b border-slate-100 sticky top-0 bg-white rounded-t-md">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          <input
                            type="text"
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded bg-slate-50 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 placeholder:text-slate-400"
                            placeholder="Search folders..."
                            value={folderSearchTerm}
                            onChange={(e) =>
                              setFolderSearchTerm(e.target.value)
                            }
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Options List */}
                      <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                        {filteredFolders.length === 0 ? (
                          <div className="px-3 py-4 text-center text-xs text-slate-500">
                            No folders found
                          </div>
                        ) : (
                          <ul className="py-1">
                            {filteredFolders.map((store) => {
                              const isSelected = documentStoreId === store.id;
                              return (
                                <li
                                  key={store.id}
                                  onClick={() => handleFolderSelect(store.id)}
                                  className={`px-3 py-2 cursor-pointer flex items-center justify-between text-sm transition-colors
                                    ${
                                      isSelected
                                        ? "bg-blue-50 text-blue-700 font-medium"
                                        : "text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                                    }
                                  `}
                                >
                                  <span className="truncate">
                                    {store.folder_name}
                                    {store.document_store_type && (
                                      <span
                                        className={`text-xs ml-1 ${
                                          isSelected
                                            ? "text-blue-500"
                                            : "text-slate-400"
                                        }`}
                                      >
                                        • {store.document_store_type}
                                      </span>
                                    )}
                                  </span>
                                  {isSelected && (
                                    <Check className="h-3.5 w-3.5 text-blue-600" />
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* --- END FOLDER DROPDOWN --- */}

              {/* --- CUSTOM TEMPLATE DROPDOWN (WITH SEARCH) --- */}
              <div className="col-span-3" ref={templateRef}>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Schema Template
                </label>
                <div className="relative group">
                  <button
                    type="button"
                    disabled={!documentStoreId}
                    onClick={() => setIsTemplateOpen(!isTemplateOpen)}
                    className={`relative w-full text-left px-3 py-2.5 pl-10 bg-white border-2 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-all duration-300 font-semibold shadow-sm hover:shadow-md flex items-center justify-between rounded-md disabled:opacity-60 disabled:cursor-not-allowed
                      ${
                        isTemplateOpen
                          ? "border-indigo-500 ring-1 ring-indigo-500"
                          : "border-slate-200 hover:border-indigo-300"
                      }
                    `}
                  >
                    <span className="truncate block pr-6">
                      {selectedTemplateObj
                        ? selectedTemplateObj.schema_name
                        : "Auto-detect"}
                    </span>

                    {/* Left Icon for Template */}
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <LayoutTemplate className="h-4 w-4 text-indigo-500" />
                    </div>

                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ChevronDown
                        className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                          isTemplateOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>

                  {/* Dropdown Panel */}
                  {isTemplateOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl animate-in fade-in zoom-in-95 duration-100">
                      {/* Search Input */}
                      <div className="p-2 border-b border-slate-100 sticky top-0 bg-white rounded-t-md">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          <input
                            type="text"
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded bg-slate-50 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 placeholder:text-slate-400"
                            placeholder="Search templates..."
                            value={templateSearchTerm}
                            onChange={(e) =>
                              setTemplateSearchTerm(e.target.value)
                            }
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Options List */}
                      <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                        <ul className="py-1">
                          {/* Default "Auto-detect" Option */}
                          <li
                            onClick={() => handleTemplateSelect("")}
                            className={`px-3 py-2 cursor-pointer flex items-center justify-between text-sm transition-colors
                              ${
                                selectedTemplateId === ""
                                  ? "bg-indigo-50 text-indigo-700 font-medium"
                                  : "text-slate-700 hover:bg-slate-50 hover:text-indigo-600"
                              }
                            `}
                          >
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-3.5 h-3.5" />
                              <span>Auto-detect</span>
                            </div>
                            {selectedTemplateId === "" && (
                              <Check className="h-3.5 w-3.5 text-indigo-600" />
                            )}
                          </li>

                          {/* Filtered Templates */}
                          {filteredTemplates.map((template) => {
                            const isSelected =
                              selectedTemplateId === template._id;
                            return (
                              <li
                                key={template._id}
                                onClick={() =>
                                  handleTemplateSelect(template._id)
                                }
                                className={`px-3 py-2 cursor-pointer flex items-center justify-between text-sm transition-colors
                                  ${
                                    isSelected
                                      ? "bg-indigo-50 text-indigo-700 font-medium"
                                      : "text-slate-700 hover:bg-slate-50 hover:text-indigo-600"
                                  }
                                `}
                              >
                                <span className="truncate">
                                  {template.schema_name}
                                </span>
                                {isSelected && (
                                  <Check className="h-3.5 w-3.5 text-indigo-600" />
                                )}
                              </li>
                            );
                          })}

                          {filteredTemplates.length === 0 && (
                            <li className="px-3 py-4 text-center text-xs text-slate-500">
                              No matching templates found
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* --- END TEMPLATE DROPDOWN --- */}

              {/* File Selection */}
              <div className="col-span-3">
                <MultiSelectDropdown
                  options={uploadedFiles.map((file) => ({
                    _id: file._id,
                    name: file.name,
                  }))}
                  selectedIds={selectedFileIds}
                  onChange={setSelectedFileIds}
                  placeholder="Choose files"
                  label="Select Files"
                  required={true}
                />
              </div>

              {/* Accuracy Level */}
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Accuracy Level
                </label>
                <div className="relative group">
                  <select
                    value={accuracyLevel}
                    onChange={(e) => setAccuracyLevel(e.target.value)}
                    disabled={!documentStoreId}
                    className="w-full appearance-none px-3 py-2.5 bg-white border border-slate-200 rounded-md text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all duration-200 cursor-pointer font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:border-violet-300"
                  >
                    <option value="HIGH">🎯 High</option>
                    <option value="MEDIUM">⚡ Medium</option>
                    <option value="LOW">🚀 Low</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Parse Button */}
              <div className="col-span-1">
                <Can permission="documents:parse">
                  <button
                    onClick={handleParse}
                    disabled={
                      isParsing ||
                      selectedFileIds.length === 0 ||
                      !documentStoreId
                    }
                    className="w-full px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-bold rounded-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 text-sm flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg hover:-translate-y-0.5"
                    title="Parse selected files"
                  >
                    {isParsing ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Parsing</span>
                      </>
                    ) : (
                      <>
                        <Zap className="w-3.5 h-3.5" />
                        <span>Parse</span>
                      </>
                    )}
                  </button>
                </Can>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="py-4">
        {isLoading ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader />
            </div>
          </div>
        ) : !documentStoreId ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-blue-100 mb-6">
                <FolderOpen className="h-12 w-12 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Select Folder to Begin
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Choose a Folder from the dropdown above to start parsing files
                with AI. Your document store contains all uploaded files ready
                for processing.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Section Header */}
            <div className="mb-3">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                Processing History
                <span className="px-2 py-0.5 text-xs font-bold bg-indigo-100 text-indigo-700 rounded-full">
                  {totalCount}
                </span>
              </h2>
            </div>

            {/* Modern Table Card */}
            <div className="bg-white rounded-md border border-slate-200 overflow-hidden shadow-sm">
              {/* Table Header */}
              <div className="bg-gradient-to-r from-slate-50 to-indigo-50/30 px-6 py-3 border-b border-slate-200">
                <div className="grid grid-cols-12 gap-6 text-xs font-bold text-slate-600 uppercase tracking-wide">
                  <div className="col-span-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-500" />
                    Document
                  </div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Duration</div>
                  <div className="col-span-2">Created</div>
                  <div className="col-span-3">Actions</div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-slate-100">
                {parseData && parseData.length > 0 ? (
                  parseData.map((item, index) => (
                    <div
                      key={item._id}
                      className="grid grid-cols-12 gap-6 px-6 py-3.5 hover:bg-slate-50/80 transition-all duration-200 group"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* File Name */}
                      <div className="col-span-3 flex items-center">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex-shrink-0 relative w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                            <FileText className="h-5 w-5 text-indigo-600" />
                            {item.file_data && item.file_data.length > 1 && (
                              <span className="absolute -top-1 -right-1 bg-indigo-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md">
                                {item.file_data.length}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            {item.file_data && item.file_data.length > 1 ? (
                              <>
                                <span className="text-sm font-semibold text-slate-900 block group-hover:text-indigo-600 transition-colors">
                                  {item.file_data.length} files
                                </span>
                                <span className="text-xs text-slate-500 truncate block">
                                  {item.file_data
                                    .map((file: any) => file.name)
                                    .join(", ")}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm font-semibold text-slate-900 truncate block group-hover:text-indigo-600 transition-colors">
                                {item.file_data?.[0]?.name || "Unknown"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-span-2 flex items-center">
                        {item.document_parse_status ? (
                          <div className="flex items-center gap-2">
                            {(() => {
                              const status = getStatusBadge(
                                item.document_parse_status,
                              );
                              const StatusIcon = status.icon;

                              const isLoading =
                                item.document_parse_status !== "DONE" &&
                                item.document_parse_status !== "FAILED";

                              return (
                                <span
                                  className={`px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 ${status.color}`}
                                >
                                  {isLoading ? (
                                    <>
                                      <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin" />
                                      {status.text}
                                    </>
                                  ) : (
                                    <>
                                      <StatusIcon className="h-3.5 w-3.5" />
                                      {status.text}
                                    </>
                                  )}
                                </span>
                              );
                            })()}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400 font-medium">
                            —
                          </span>
                        )}
                      </div>

                      {/* Duration */}
                      <div className="col-span-2 flex items-center">
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 group-hover:bg-slate-100 transition-colors">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <span className="text-sm font-bold text-slate-700">
                            {item.document_parse_duration
                              ? `${item.document_parse_duration}s`
                              : "—"}
                          </span>
                        </div>
                      </div>

                      {/* Created Date */}
                      <div className="col-span-2 flex items-center">
                        <span className="text-sm text-slate-600 font-medium">
                          {formatDate(item.createdAt)}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-span-3 flex items-center gap-2">
                        <button
                          onClick={() => handleViewResult(item)}
                          className="group/btn p-2.5 rounded-xl border-2 border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-md"
                          title="View Result"
                        >
                          <Eye className="h-4 w-4 text-slate-600 group-hover/btn:text-indigo-600 transition-colors" />
                        </button>
                        <button
                          onClick={() => handleViewLogs(item)}
                          className="group/btn p-2.5 rounded-xl border-2 border-slate-200 bg-white hover:bg-violet-50 hover:border-violet-300 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-md"
                          title="View Details"
                        >
                          <ListIcon className="h-4 w-4 text-slate-600 group-hover/btn:text-violet-600 transition-colors" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-3">
                      <FileText className="h-8 w-8 text-slate-300" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">
                      No Parsed Files Yet
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto">
                      Select files and click parse to process documents
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modern Pagination */}
            {totalPages > 1 && totalCount > 0 && (
              <div className="mt-4 bg-white rounded-lg border border-slate-200 px-6 py-3 mb-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-600">
                    {totalCount > 0 && (
                      <span>
                        Showing{" "}
                        <span className="text-indigo-600 font-bold">
                          {startItem}–{endItem}
                        </span>{" "}
                        of{" "}
                        <span className="text-slate-900 font-bold">
                          {totalCount}
                        </span>{" "}
                        files
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2.5 rounded-xl border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 disabled:hover:scale-100"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-1.5">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`min-w-[40px] h-10 px-4 rounded-xl text-sm font-bold transition-all duration-200 ${
                                currentPage === pageNum
                                  ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-200 scale-105"
                                  : "bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:scale-105 active:scale-95"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2.5 rounded-xl border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 disabled:hover:scale-100"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <JsonViewPopup
        isOpen={showJsonPopup}
        onClose={() => setShowJsonPopup(false)}
        data={selectedData}
        title={popupTitle}
      />

      {selectedItem && (
        <LogsPopup
          isOpen={showLogsPopup}
          onClose={() => setShowLogsPopup(false)}
          data={selectedItem}
          title={popupTitle}
        />
      )}
    </div>
  );
};

export default ParseFile;
